import { ColDef } from "ag-grid-community";
import { AgGridReactProps } from "ag-grid-react";
import { Card, message } from "antd";
import { Dayjs } from "dayjs";
import React, { useState } from "react";
import { AppButton } from "../../../../atoms/Button";
import Grid from "../../../../atoms/Grid";
import { formatCurrency, formatDateByType } from "../../../../libs/utils";
import { usePendingRequestsActions } from "../../../PendingApprovalRequets/api/useRefreshPendingRequests";
import { postReceiptChequeClear } from "../../Receipts/api/receiptActions";
import { ClearingDatePicker, isClearingDateValid } from "../../Receipts/ClearingDatePicker";
import { useClearCheque } from "./hook/useClearCheque";

interface ChequeRow {
  id: string;
  receiptNo: string;
  chequeNo: string;
  chequeDate: string;
  paidDate: string;
  amount: number;
  data?: any;
  totalAmount?: number;
}

const ChequeClearModal = ({ data, onSuccess, setVisible, isReceiptEnabled }: any) => {
  const { clearCheque, loading } = useClearCheque();
  const [selectedPdcIds, setSelectedPdcIds] = useState<(string | { subPdcId: string })[]>([]);
  const [clearingDate, setClearingDate] = useState<Dayjs | null>(null);
  const [showClearingDateError, setShowClearingDateError] = useState(false);
  const { refreshPendingRequests } = usePendingRequestsActions();
  let isPartial = data?.params?.data?.paymentMethodType?.toLowerCase() === "partial";
  const isReceiptClearFlow = Boolean(isReceiptEnabled);

  console.log(data?.params?.data, "isReceiptClearFlow");
  const chequeRowData = isPartial ? data?.params?.data?.subPdcs : [data?.params?.data];

  const documentNo =
    data?.params?.data?.documentNo ||
    data?.params?.data?.paymentDetails?.documentNo ||
    data?.params?.data?.paymentDetails?.chequeNo ||
    "";

  const chequeDate =
    data?.params?.data?.paidDate ||
    data?.params?.data?.paymentDetails?.chequeDate ||
    data?.params?.data?.paymentDetails?.depositDate ||
    data?.params?.data?.chequeDate ||
    null;

  const totalAmount =
    data?.params?.data?.totalAmount ?? data?.params?.data?.actualAmount ?? data?.params?.data?.amount ?? 0;

  const receiptDisplayRow = isReceiptClearFlow
    ? {
        ...data?.params?.data,
        receiptNo: data?.params?.data?.receiptNo ?? "",
        documentNo,
        chequeDate,
        totalAmount,
      }
    : null;

  const filterCheques =
    chequeRowData.filter(
      (pdc: any) => pdc?.status?.toLowerCase() === "paid" && pdc.paymentReceiptId && !pdc?.isCleared,
    ) || [];

  const chequeColumnDefs: ColDef<ChequeRow>[] = [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 50,
      flex: 1,
    },
    {
      headerName: "Receipt No",
      field: "receiptNo",
      maxWidth: 150,
      width: 150,
    },
    {
      headerName: "Cheque No",
      field: "documentNo",
      minWidth: 150,
      width: 150,
    },

    {
      headerName: "Cheque Date",
      field: "chequeDate",
      minWidth: 160,
      width: 160,
      valueFormatter: ({ value, data }) => formatDateByType(value ?? data?.paidDate),
    },
    {
      headerName: "Amount",
      maxWidth: 150,
      width: 150,
      type: "numericColumn",
      valueGetter: ({ data }) => data?.totalAmount ?? data?.amount ?? 0,
      valueFormatter: ({ value }) => formatCurrency(value),
    },
  ];

  const onSelectionChanged: AgGridReactProps["onSelectionChanged"] = ({ api }) => {
    const pdcIds: (string | { subPdcId: string })[] = [];

    for (const node of api.getSelectedNodes()) {
      const { pdcId, subPdcId } = node?.data || {};
      if (isPartial && subPdcId) pdcIds.push({ subPdcId });
      else pdcIds.push(pdcId);
    }
    if (pdcIds.length > 0) setSelectedPdcIds(pdcIds);
    else setSelectedPdcIds([]);
  };

  const handleClearingDateChange = (date: Dayjs | null) => {
    setClearingDate(date);
    if (isClearingDateValid(date)) {
      setShowClearingDateError(false);
    }
  };

  const handleMarkAsCleared = async () => {
    if (!isClearingDateValid(clearingDate)) {
      setShowClearingDateError(true);
      return;
    }

    const clearedDate = formatDateByType(clearingDate, "api")!;

    try {
      if (isReceiptEnabled) {
        await postReceiptChequeClear({
          paymentReceiptId: data?.params?.data?.paymentReceiptId,
          isCleared: true,
          clearedDate,
        });
        message.success("Cheque cleared successfully");
        onSuccess();
        return;
      }

      const payload: any = {
        leaseId: data?.params?.data?.leaseId,
        pdcId: data?.params?.data?.pdcId,
        isCleared: true,
        clearedDate,
      };

      if (isPartial) {
        payload["subPdc"] = selectedPdcIds;
      }
      const result = await clearCheque(payload);

      if (result?.success) {
        message.success("Cheque cleared successfully");
        await refreshPendingRequests();
        onSuccess();
      }
    } catch (err: any) {
      message?.error(err?.response?.data?.error || "Failed to update invoice");
    }
  };

  return (
    <Card bodyStyle={{ padding: 0 }}>
      <Grid
        data={isReceiptClearFlow ? [receiptDisplayRow] : isPartial ? filterCheques : chequeRowData}
        columns={chequeColumnDefs}
        rowSelection="multiple"
        className="mark-clear-grid"
        height={filterCheques?.length > 1 ? filterCheques?.length * 80 + "px" : "120px"}
        onSelectionChanged={onSelectionChanged}
        getRowId={(params) => params.data.id}
      />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1rem 0.5rem 0" }}>
        <AppButton variant="secondary" onClick={() => setVisible({ status: false, index: 0 })}>
          Cancel
        </AppButton>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <ClearingDatePicker
            value={clearingDate}
            onChange={handleClearingDateChange}
            showValidation={showClearingDateError}
          />
          <AppButton disabled={selectedPdcIds?.length === 0} loading={loading} onClick={() => handleMarkAsCleared()}>
            Mark as Cleared
          </AppButton>
        </div>
      </div>
    </Card>
  );
};

export default ChequeClearModal;
